import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PractitionerTypeahead } from '@/components/appointments/PractitionerTypeahead';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { usePatientTreatmentCourseStepSlots } from '@/hooks/usePortalTreatmentCourses';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { useClinicPractitionerUserIds } from '@/hooks/useClinicPractitionerUserIds';
import {
  NO_PRACTITIONER_MATCH_ID,
  filterPractitionerMembers,
  visiblePractitionerIds,
  withStickySelectedMembers,
} from '@/lib/practitionerRole';
import { usePatientStaffAssignments } from '@/hooks/usePatientStaffAssignments';
import { useTreatmentCourseStepSlots } from '@/hooks/useBookTreatmentCourse';
import {
  practitionerSelectionAfterSuggestion,
  suggestedLinkedPractitionerIds,
} from '@/lib/practitionerSuggestions';
import {
  civilDaysInRange,
  groupSlotsByCivilDay,
  nextSlotPickerDay,
  practitionerDisplayName,
  practitionerNameMap,
  resolvePractitionerLabel,
  workspaceSlotSearchRange,
} from '@/lib/courseStepSlotPicker';
import { formatDanishCivilDate } from '@/lib/danishDate';
import {
  clampIsoToNow,
  futureSlots,
  localDayEndIso,
  localDayStartIso,
  roundNowMs,
} from '@/lib/treatmentCourseWizard';
import { formatAppointmentClock } from '@/utils/appointmentTimeFormat';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { IntervalUnit } from '@/types/treatmentCourses';
import type { TreatmentCourseStepSlot } from '@/hooks/useBookTreatmentCourse';

function dayParts(civil: string, language: string): { weekday: string; day: string } {
  const date = new Date(`${civil}T12:00:00`);
  const locale = language.startsWith('da') ? 'da-DK' : 'en-GB';
  return {
    weekday: date.toLocaleDateString(locale, { weekday: 'short' }).replace('.', ''),
    day: String(date.getDate()),
  };
}

export function CourseStepSlotPicker({
  clinicId,
  bookedStepId,
  courseId,
  unbooked,
  bookedStartsAt,
  previousStartsAt,
  intervalValue,
  intervalUnit,
  toleranceValue,
  toleranceUnit,
  warningWindowStart,
  warningWindowEnd,
  practitionerOptions,
  stickyPractitionerIds,
  patientId,
  selectedSlot,
  onSelectSlot,
  enabled,
}: {
  clinicId: string;
  bookedStepId: string;
  courseId?: string | null;
  /** Patient whose Tilknytninger can be suggested. Omit when the viewer cannot read them. */
  patientId?: string | null;
  unbooked: boolean;
  bookedStartsAt?: string | null;
  previousStartsAt?: string | null;
  intervalValue?: number | null;
  intervalUnit?: IntervalUnit;
  toleranceValue?: number | null;
  toleranceUnit?: IntervalUnit;
  warningWindowStart?: string | null;
  warningWindowEnd?: string | null;
  practitionerOptions?: Array<{ id: string; name: string }>;
  /** Already assigned people stay in the list even without the Behandler flag. */
  stickyPractitionerIds?: string[];
  selectedSlot?: TreatmentCourseStepSlot | null;
  onSelectSlot: (slot: TreatmentCourseStepSlot) => void;
  enabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const fallbackName = t('treatment_courses.practitioner_fallback');
  const portal = Boolean(courseId);
  const { data: members } = useClinicMembers(portal ? null : clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(portal ? null : clinicId);
  const practitionerIdQuery = useClinicPractitionerUserIds(clinicId, portal);
  const [range, setRange] = useState(() =>
    workspaceSlotSearchRange({
      unbooked,
      bookedStartsAt,
      previousStartsAt,
      intervalValue,
      intervalUnit,
      toleranceValue,
      toleranceUnit,
      warningWindowStart,
      warningWindowEnd,
    }),
  );
  const [userPickedDay, setUserPickedDay] = useState<string | null>(null);
  const [practitionerIds, setPractitionerIds] = useState<string[]>([]);
  const practitionerTouch = useRef(false);
  const assignments = usePatientStaffAssignments(patientId, clinicId);

  useEffect(() => {
    const next = workspaceSlotSearchRange({
      unbooked,
      bookedStartsAt,
      previousStartsAt,
      intervalValue,
      intervalUnit,
      toleranceValue,
      toleranceUnit,
      warningWindowStart,
      warningWindowEnd,
    });
    setRange(next);
    setUserPickedDay(null);
    setPractitionerIds([]);
    practitionerTouch.current = false;
  }, [
    bookedStepId,
    bookedStartsAt,
    courseId,
    patientId,
    intervalUnit,
    intervalValue,
    previousStartsAt,
    toleranceUnit,
    toleranceValue,
    unbooked,
    warningWindowEnd,
    warningWindowStart,
  ]);

  const searchNowMs = useMemo(() => roundNowMs(), [range.rangeStart, range.rangeEnd]);
  const windowStart = clampIsoToNow(localDayStartIso(range.rangeStart), searchNowMs);
  const windowEnd = localDayEndIso(range.rangeEnd);
  const staffInput = useMemo(
    () =>
      enabled && !portal
        ? {
            clinicId,
            bookedStepId,
            windowStart,
            windowEnd,
            previousStartsAt: previousStartsAt ?? null,
            practitionerIds,
          }
        : null,
    [
      bookedStepId,
      clinicId,
      enabled,
      portal,
      practitionerIds,
      previousStartsAt,
      windowEnd,
      windowStart,
    ],
  );
  const portalCandidateIds = useMemo(
    () => (practitionerOptions ?? []).map((row) => row.id).filter(Boolean),
    [practitionerOptions],
  );
  const stickyKey = (stickyPractitionerIds ?? []).join(',');
  const selectedSlotPractitionerKey = (selectedSlot?.practitioner_ids ?? []).join(',');
  const portalSearchIds = useMemo(() => {
    if (!portal) return practitionerIds;
    if (practitionerIds.length > 0) return practitionerIds;
    if (practitionerIdQuery.isError) return [];
    if (!practitionerIdQuery.isSuccess) return null;
    const visible = visiblePractitionerIds(portalCandidateIds, practitionerIdQuery.data ?? [], [
      ...stickyKey.split(',').filter(Boolean),
      ...selectedSlotPractitionerKey.split(',').filter(Boolean),
    ]);
    return visible.length > 0 ? visible : [NO_PRACTITIONER_MATCH_ID];
  }, [
    portal,
    portalCandidateIds,
    practitionerIdQuery.data,
    practitionerIdQuery.isError,
    practitionerIdQuery.isSuccess,
    practitionerIds,
    selectedSlotPractitionerKey,
    stickyKey,
  ]);
  const portalInput = useMemo(
    () =>
      enabled && courseId && portalSearchIds != null
        ? {
            courseId,
            stepId: bookedStepId,
            windowStart,
            windowEnd,
            practitionerIds: portalSearchIds,
          }
        : null,
    [bookedStepId, courseId, enabled, portalSearchIds, windowEnd, windowStart],
  );
  const staffQuery = useTreatmentCourseStepSlots(staffInput, Boolean(staffInput));
  const portalQuery = usePatientTreatmentCourseStepSlots(portalInput, Boolean(portalInput));
  const slots = portal ? portalQuery.data : staffQuery.data;
  const isLoading = portal ? portalQuery.isLoading : staffQuery.isLoading;
  const visibleSlots = useMemo(() => futureSlots(slots ?? []), [slots]);
  const grouped = useMemo(() => groupSlotsByCivilDay(visibleSlots), [visibleSlots]);
  const days = useMemo(
    () => civilDaysInRange(range.rangeStart, range.rangeEnd),
    [range.rangeEnd, range.rangeStart],
  );
  const selectedDay = nextSlotPickerDay({
    days,
    grouped,
    current: userPickedDay ?? '',
    userPicked: userPickedDay != null,
  });
  const daySlots = grouped.get(selectedDay) ?? [];

  const names = useMemo(() => {
    const map = practitionerNameMap(practitionerOptions ?? []);
    for (const member of members ?? []) {
      const label = practitionerDisplayName(member.name || member.email, fallbackName);
      if (label !== fallbackName) map.set(member.user_id, label);
    }
    return map;
  }, [fallbackName, members, practitionerOptions]);

  const filterPeople = useMemo(() => {
    const fromOptions = (practitionerOptions ?? [])
      .map((row) => ({
        id: row.id,
        name: resolvePractitionerLabel(row.id, names, fallbackName),
      }))
      .filter((row) => row.id);
    if (fromOptions.length > 0) {
      if (!portal) return uniquePeople(fromOptions);
      if (practitionerIdQuery.isError) return uniquePeople(fromOptions);
      if (!practitionerIdQuery.isSuccess) {
        const sticky = new Set(stickyKey.split(',').filter(Boolean));
        return uniquePeople(fromOptions.filter((row) => sticky.has(row.id)));
      }
      const allowed = new Set(
        visiblePractitionerIds(
          fromOptions.map((row) => row.id),
          practitionerIdQuery.data ?? [],
          [
            ...stickyKey.split(',').filter(Boolean),
            ...selectedSlotPractitionerKey.split(',').filter(Boolean),
          ],
        ),
      );
      return uniquePeople(fromOptions.filter((row) => allowed.has(row.id)));
    }
    const all = members ?? [];
    const listed = rolesReady
      ? withStickySelectedMembers(filterPractitionerMembers(all, roles ?? []), all, practitionerIds)
      : all;
    return uniquePeople(
      listed.map((member) => ({
        id: member.user_id,
        name: resolvePractitionerLabel(member.user_id, names, fallbackName),
      })),
    );
  }, [
    fallbackName,
    members,
    names,
    portal,
    practitionerIdQuery.data,
    practitionerIdQuery.isError,
    practitionerIdQuery.isSuccess,
    practitionerIds,
    practitionerOptions,
    roles,
    rolesReady,
    selectedSlotPractitionerKey,
    stickyKey,
  ]);

  const searchPeople = useMemo(() => {
    if ((practitionerOptions ?? []).length === 0 && !portal && !rolesReady) return [];
    return filterPeople;
  }, [filterPeople, portal, practitionerOptions, rolesReady]);

  const behandlerIds = useMemo(() => {
    if (portal) {
      if (!practitionerIdQuery.isSuccess) return new Set<string>();
      return new Set(practitionerIdQuery.data ?? []);
    }
    if (!rolesReady) return new Set<string>();
    return new Set(
      filterPractitionerMembers(members ?? [], roles ?? []).map((member) => member.user_id),
    );
  }, [members, portal, practitionerIdQuery.data, practitionerIdQuery.isSuccess, roles, rolesReady]);

  const suggestedIds = useMemo(() => {
    if (!patientId || assignments.isLoading) return [];
    return suggestedLinkedPractitionerIds({
      assignmentUserIds: (assignments.data ?? []).map((row) => row.user_id),
      behandlerIds,
      selectableIds: new Set(searchPeople.map((person) => person.id)),
    });
  }, [assignments.data, assignments.isLoading, behandlerIds, patientId, searchPeople]);

  useEffect(() => {
    if (practitionerTouch.current) return;
    if (!patientId || assignments.isLoading) return;
    if (portal && !practitionerIdQuery.isSuccess && !practitionerIdQuery.isError) return;
    if (!portal && !rolesReady && (practitionerOptions ?? []).length === 0) return;
    const next = practitionerSelectionAfterSuggestion({
      currentIds: practitionerIds,
      suggestedIds,
      userAdjusted: false,
    });
    if (next) setPractitionerIds(next);
  }, [
    assignments.isLoading,
    patientId,
    portal,
    practitionerIdQuery.isError,
    practitionerIdQuery.isSuccess,
    practitionerIds,
    practitionerOptions,
    rolesReady,
    suggestedIds,
  ]);

  return (
    <div className="space-y-s3" data-testid="course-step-slot-picker">
      <p className={cn(TYPE.small, 'text-ds-muted')} data-testid="course-step-slot-range">
        {t('treatment_courses.workspace_slot_range', {
          from: formatDanishCivilDate(range.rangeStart),
          to: formatDanishCivilDate(range.rangeEnd),
        })}
      </p>
      <div className="grid gap-s3 nav:grid-cols-2">
        <DanishDateField
          name="course-step-date-from"
          label={t('treatment_courses.book_date_from')}
          value={range.rangeStart}
          onChange={(rangeStart) => {
            if (!rangeStart) return;
            setUserPickedDay(null);
            setRange((current) => ({
              rangeStart,
              rangeEnd: current.rangeEnd < rangeStart ? rangeStart : current.rangeEnd,
            }));
          }}
        />
        <DanishDateField
          name="course-step-date-to"
          label={t('treatment_courses.book_date_to')}
          value={range.rangeEnd}
          onChange={(rangeEnd) => {
            if (!rangeEnd) return;
            setUserPickedDay(null);
            setRange((current) => ({
              rangeStart: current.rangeStart > rangeEnd ? rangeEnd : current.rangeStart,
              rangeEnd,
            }));
          }}
        />
      </div>

      <PractitionerTypeahead
        people={searchPeople}
        selectedIds={practitionerIds}
        onChange={(ids) => {
          practitionerTouch.current = true;
          setPractitionerIds(ids);
        }}
        legend={t('treatment_courses.field_practitioners')}
        searchPlaceholder={t('treatment_courses.book_practitioners_search')}
        emptyLabel={t('treatment_courses.book_practitioners_search_empty')}
      />

      <div className="flex gap-s2 overflow-x-auto pb-s1" role="tablist">
        {days.map((civil) => {
          const parts = dayParts(civil, i18n.language);
          const active = civil === selectedDay;
          const hasSlots = (grouped.get(civil)?.length ?? 0) > 0;
          return (
            <button
              key={civil}
              type="button"
              role="tab"
              aria-selected={active}
              aria-disabled={!hasSlots}
              disabled={!hasSlots}
              onClick={() => {
                if (!hasSlots) return;
                setUserPickedDay(civil);
              }}
              className={cn(
                'min-w-[4.25rem] rounded-ctl border px-s2 py-s2 text-center',
                active
                  ? 'border-ds-teal bg-ds-teal-tint text-ds-teal'
                  : hasSlots
                    ? 'border-ds-line bg-ds-card text-ds-text hover:bg-ds-sunk'
                    : 'cursor-not-allowed border-ds-line bg-ds-sunk text-ds-muted opacity-50',
              )}
            >
              <span className={cn(TYPE.meta, 'block uppercase')}>{parts.weekday}</span>
              <span className={cn(TYPE.body, 'block font-semibold tabular-nums')}>{parts.day}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>
          {t('treatment_courses.book_slots_loading')}
        </p>
      ) : null}

      <div className="max-h-64 overflow-y-auto">
        {!isLoading && daySlots.length === 0 ? (
          <p className={cn(TYPE.small, 'text-ds-muted')}>
            {t('treatment_courses.book_slots_empty')}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-s2 nav:grid-cols-3">
            {daySlots.map((slot) => {
              const selected =
                selectedSlot?.starts_at === slot.starts_at &&
                selectedSlot.practitioner_ids.join() === slot.practitioner_ids.join();
              return (
                <Button
                  key={`${slot.starts_at}-${slot.practitioner_ids.join('-')}`}
                  type="button"
                  variant={selected ? 'primary' : 'secondary'}
                  className="h-auto justify-start px-s3 py-s2"
                  onClick={() => onSelectSlot(slot)}
                >
                  <span className="block text-left">
                    <span className={cn(TYPE.body, 'block font-medium')}>
                      {formatAppointmentClock(new Date(slot.starts_at), i18n.language)}
                    </span>
                    <span className={cn(TYPE.meta, 'block text-ds-muted')}>
                      {slot.practitioner_ids
                        .map((id) => resolvePractitionerLabel(id, names, fallbackName))
                        .join(', ')}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function uniquePeople(
  rows: Array<{ id: string; name: string }>,
): Array<{ id: string; name: string }> {
  const seen = new Set<string>();
  const out: Array<{ id: string; name: string }> = [];
  for (const row of rows) {
    if (!row.id || seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}
